import React, { forwardRef, memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import Checkbox from "./Checkbox";
import Loading from "../../assets/images/Loading-v2.gif";
import Radio from "./Radio";
import ArrowUp from "./Icons/ArrowUp";
import ArrowDown from "./Icons/ArrowDown";
import SortIcon from "./Icons/SortIcon";

const Table = forwardRef(
  (
    {
      columns = [],
      data = [],
      onRowClick,
      onColClick,
      renderRow,
      emptyText = "No Data to Display",
      rowKey = "id",
      className = "",
      fixedHeader = false,
      selectable = false,
      checkboxComponent = null,
      onRowSelect = null,
      loading = false,
      selectedRows = [],
      onHeaderMouseEnter = null,
      onHeaderMouseLeave = null,
      bordered = false,
      stripped = true,
      hoverable = true,
      infiniteScroll = false,
      sentinelRef = null,
      loadingMore = false,
      hasMore = false,
      isMultiSelect = true,
      sort,
      onSortChange,
      ...props
    },
    ref,
  ) => {
    const onClickRow = useCallback(
      (row) => {
        onRowClick?.(row);
      },
      [onRowClick],
    );

    const onClickCol = useCallback(
      (row, data, col) => {
        onColClick?.(row, data, col);
      },
      [onColClick],
    );

    const onSelectRow = useCallback(
      (row) => {
        const key = row[rowKey];
        let updated = [];
        if (isMultiSelect) {
          updated = selectedRows.includes(key)
            ? selectedRows.filter((k) => k !== key)
            : [...selectedRows, key];
        } else updated = [key];
        onRowSelect?.(updated);
      },
      [rowKey, selectedRows, onRowSelect, isMultiSelect],
    );

    const onAllSelect = useCallback(
      (checked) => {
        const updated = checked ? data.map((item) => item[rowKey]) : [];
        onRowSelect?.(updated);
      },
      [data, rowKey, onRowSelect],
    );

    const selectedSet = useMemo(() => new Set(selectedRows), [selectedRows]);

    const handleSort = (event, key) => {
      if (!onSortChange) return;

      if (!sort || sort.key !== key) {
        onSortChange({
          key,
          direction: "asc",
        });
        return;
      }

      onSortChange({
        key,
        direction: sort.direction === "asc" ? "desc" : "asc",
      });
    };

    const renderSortIcon = (column) => {
      if (!column.sortable) return null;

      if (sort?.key !== column.key)
        return <SortIcon size={8} className="table-sort-icon" />;

      return sort.direction === "asc" ? (
        <ArrowUp size={12} className="table-sort-icon" />
      ) : (
        <ArrowDown size={12} className="table-sort-icon" />
      );
    };

    return (
      <div
        className={`tableContainer${fixedHeader ? " stickyHeader" : ""}${bordered ? " bordered" : ""
          }${stripped ? " stripped" : ""}${hoverable ? " hoverable" : ""
          } ${className}`}
        ref={ref}
        {...props}
      >
        <table className="tableSec">
          <thead>
            <tr className="tableHeadRow">
              {selectable ? (
                <th className={`tableHead${selectable ? " rowSelectable" : ""
                  }`}>
                  {checkboxComponent ? (
                    checkboxComponent
                  ) : isMultiSelect ? (
                    <Checkbox
                      size="md"
                      indeterminate={
                        selectedRows.length > 0 &&
                        selectedRows.length < data.length
                      }
                      checked={
                        selectedRows.length === data.length && data.length > 0
                      }
                      onChange={onAllSelect}
                    />
                  ) : null}
                </th>
              ) : null}
              {columns.map((col, colIndex) => (
                <th
                  key={String(col.key)}
                  className={`tableHead ${col.key}${col.sortable ? " sortable" : ""} ${col.className ?? ""}`}
                  onClick={(event) =>
                    col.sortable && handleSort(event, col.key)
                  }
                >
                  {col.renderHeader ? col.renderHeader(col) : col.title}
                  {col.sortable
                    ? col.sortableComponent || renderSortIcon(col)
                    : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr className="tableBodyRow">
                <td
                  colSpan={(selectable ? columns.length + 1 : columns.length) || 0}
                  className="tableBodyRowEmptyRow"
                >
                  {emptyText}
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => {
                if (renderRow) return renderRow(row, rowIndex, columns);
                return (
                  <TableRow
                    key={row[rowKey]}
                    row={row}
                    columns={columns}
                    rowKey={rowKey}
                    selectable={selectable}
                    isSelected={selectedSet.has(row[rowKey])}
                    onRowClick={onClickRow}
                    onColClick={onClickCol}
                    onSelectRow={onSelectRow}
                    checkboxComponent={checkboxComponent}
                    isMultiSelect={isMultiSelect}
                  />
                );
              })
            )}
            {infiniteScroll && loadingMore && (
              <tr className="tableBodyRow">
                <td colSpan={(selectable ? columns.length + 1 : columns.length) || 0} className="tableBodyRowEmptyRow">
                  <img className="loadingPng" src={Loading} alt="Loading..." />
                </td>
              </tr>
            )}
            {infiniteScroll && hasMore && (
              <tr>
                <td colSpan={(selectable ? columns.length + 1 : columns.length) || 0}>
                  <div
                    ref={sentinelRef}
                    style={{
                      height: "1px",
                    }}
                  />
                </td>
              </tr>
            )}
            {/* {infiniteScroll && !hasMore && data.length > 0 && (
              <tr className="tableBodyRow">
                <td
                  colSpan={columns.length || 0}
                  className="tableBodyRowEmptyRow"
                >
                  {emptyText}
                </td>
              </tr>
            )} */}
          </tbody>
        </table>
      </div>
    );
  },
);

export const TableRow = memo(
  ({
    row,
    columns,
    rowKey,
    selectable,
    isSelected,
    onRowClick,
    onColClick,
    onSelectRow,
    checkboxComponent,
    isMultiSelect = true,
  }) => {
    return (
      <tr
        className="tableBodyRow"
        onClick={() => onRowClick?.(row)}
        tabIndex={0}
      >
        {selectable ? (
          <td className={`tableData${selectable ? " rowSelectable" : ""}`}>
            {checkboxComponent ? (
              checkboxComponent
            ) : isMultiSelect ? (
              <Checkbox
                size="md"
                checked={isSelected}
                onChange={() => onSelectRow(row)}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <Radio
                size="md"
                checked={isSelected}
                onChange={() => onSelectRow(row)}
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </td>
        ) : null}
        {columns.map((col, colIndex) => (
          // <td
          //   key={colIndex}
          //   className={`tableData ${col.key}${col.className ?? ""}`}
          //   onClick={() => onColClick?.(row, row[col.key], col)}
          // >

          <td
            key={colIndex}
            className={`tableData ${col.key} ${col.className ?? ""}`}
            onClick={() => onColClick?.(row, row[col.key], col)}
          >
            {col.render ? col.render(row[col.key], row) : row[col.key] || "-"}
          </td>
        ))}
      </tr>
    );
  },
);

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      render: PropTypes.func,
      className: PropTypes.string,
    }),
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Table;
